import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import PortalPopup from "../../components/PortalPopup";
import NavigationPanel from "../../components/NavigationPanel";
import Footer from "../../components/Footer";
import SettingNav from "../../components/settingNav";
import Toggle from "../../components/Toggle";
import ToastMsg from "../../components/ToastMsg";
import { Monitor } from "iconsax-reactjs";
import TabState from "../../components/TabState";
import { userService } from "../../services/userService";

const API_URL = process.env.REACT_APP_API_URL;

const SettingsAccount3Page = () => {
  const navigate = useNavigate();
  const [isToastMsgOpen, setToastMsgOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(
    "Changes saved successfully",
  );
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setIsLoading(true);
      const result = await userService.getMe();
      if (result?.data) {
        setUserEmail(result.data.email || "");
        setTwoFactorEnabled(result.data.twoFactorEnabled || false);
      }
    } catch (err) {
      console.error("Failed to load user data:", err);
      setError("Failed to load user data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError("");
      if (newPassword || confirmPassword || currentPassword) {
        if (!currentPassword) {
          setError("Current password is required");
          return;
        }
        if (!newPassword) {
          setError("New password is required");
          return;
        }
        if (newPassword !== confirmPassword) {
          setError("New passwords do not match");
          return;
        }
        if (newPassword.length < 6) {
          setError("New password must be at least 6 characters");
          return;
        }
        const token = localStorage.getItem("authToken");
        if (!token) {
          setError("Not authenticated");
          navigate("/login");
          return;
        }
        const updatePayload = { currentPassword, newPassword };
        const response = await fetch(`${API_URL}/api/users/update-profile`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatePayload),
        });
        const result = await response.json();
        if (!response.ok)
          throw new Error(result.message || "Failed to update password");
        if (result.user) {
          localStorage.setItem("authUser", JSON.stringify(result.user));
          userService._cachedUser = result.user;
        }
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setToastMessage("Password changed successfully");
        openToastMsg();
      } else {
        setError("Please enter your current password and new password");
      }
    } catch (err) {
      setError(err.message || "Failed to change password");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  };

  const handleToggle2FA = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        setError("Not authenticated");
        navigate("/login");
        return;
      }
      const response = await fetch(`${API_URL}/api/users/toggle-2fa`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled: !twoFactorEnabled }),
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.message || "Failed to toggle 2FA");
      setTwoFactorEnabled(!twoFactorEnabled);
      setToastMessage(
        `Two-factor authentication ${!twoFactorEnabled ? "enabled" : "disabled"}`,
      );
      openToastMsg();
    } catch (err) {
      setError(err.message || "Failed to toggle 2FA");
    }
  };

  const closeLogoutConfirmation = useCallback(
    () => setLogoutConfirmationOpen(false),
    [],
  );
  const openToastMsg = useCallback(() => setToastMsgOpen(true), []);
  const closeToastMsg = useCallback(() => setToastMsgOpen(false), []);

  const tabs = [
    { label: "Profile", to: "/setting-account" },
    { label: "Connected accounts", to: "/setting-account2" },
    { label: "Security", to: "/setting-account3" },
    { label: "Delete account", to: "/setting-account4" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');

        html, body { background: rgb(241, 245, 249) !important; }

        .dd-root * { box-sizing: border-box; }
        .dd-root {
          font-family: 'DM Sans', sans-serif;
          background: rgb(241, 245, 249) !important;
          min-height: 100vh;
        }
        .dd-root.bg-white,
        .dd-root.bg-neutrals-neutral-50,
        .dd-root.bg-gray-50 {
          background: rgb(241, 245, 249) !important;
        }

        .dd-card {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          transition: box-shadow 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .dd-section-divider {
          border: none;
          border-top: 1px solid #E2E8F0;
          margin: 0;
        }

        .dd-section-label {
          font-size: 14px;
          font-weight: 700;
          color: #0F172A;
          margin: 0 0 12px 0;
        }

        .dd-section-sublabel {
          font-size: 13px;
          color: #64748B;
          margin: 2px 0 0 0;
        }

        .dd-btn-secondary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #0F172A;
          cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          transition: border-color 0.15s, background 0.15s;
        }
        .dd-btn-secondary:hover { border-color: #94A3B8; background: #F8FAFC; }
        .dd-btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

        .dd-btn-primary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px;
          background: #2563EB; border: 1px solid #2563EB; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #ffffff;
          cursor: pointer;
          transition: background 0.15s, box-shadow 0.15s;
        }
        .dd-btn-primary:hover { background: #1D4ED8; box-shadow: 0 4px 12px rgba(37,99,235,0.25); }
        .dd-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

        .dd-input {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: #1E293B;
          background: #FFFFFF;
          outline: none;
          transition: border-color 0.15s;
        }
        .dd-input:focus { border-color: #2563EB; }
        .dd-input:disabled { background: #F8FAFC; color: #64748B; cursor: not-allowed; }

        .dd-login-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid #F1F5F9;
        }
        .dd-login-item:last-child {
          border-bottom: none;
        }
      `}</style>

      <div
        className="dd-root w-full overflow-visible"
        style={{ background: "rgb(241, 245, 249)", minHeight: "100vh" }}
      >
        <NavigationPanel />

        <div
          style={{
            position: "absolute",
            top: 136,
            width: "100%",
            padding: "0 96px 64px",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          {/* Page heading */}
          <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: 24 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: "#0F172A",
              }}
            >
              Settings
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>
              Get your billing information, generate/review invoices.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "flex-start", gap: 32 }}>
            <SettingNav />

            <div
              className="dd-card"
              style={{
                flex: 1,
                padding: 28,
                display: "flex",
                flexDirection: "column",
                gap: 24,
              }}
            >
              {/* Card header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  paddingBottom: 20,
                  borderBottom: "1px solid #E2E8F0",
                }}
              >
                <div>
                  <div
                    style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}
                  >
                    Account
                  </div>
                  <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>
                    Allow users to configure how and when communication is
                    initiated with debtors.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                  <button
                    className="dd-btn-secondary"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="dd-btn-primary"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div
                  style={{
                    padding: "12px 16px",
                    background: "#FEF2F2",
                    border: "1px solid #FECACA",
                    borderRadius: 8,
                    color: "#991B1B",
                    fontSize: 13,
                  }}
                >
                  {error}
                </div>
              )}

              {/* Loading state */}
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: 40,
                  }}
                >
                  <p style={{ color: "#64748B" }}>Loading...</p>
                </div>
              ) : (
                <>
                  {/* Sub-tabs */}
                  <div style={{ display: "flex", gap: 8, marginBottom: -8 }}>
                    {tabs.map((tab) => (
                      <TabState key={tab.to} label={tab.label} to={tab.to} />
                    ))}
                  </div>

                  <hr className="dd-section-divider" />

                  {/* ===== PASSWORD SECTION ===== */}
                  {/* Current Password */}
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    <label className="dd-section-label" style={{ margin: 0 }}>
                      Current Password
                    </label>
                    <input
                      type="password"
                      className="dd-input"
                      placeholder="Enter your password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                  </div>

                  <hr className="dd-section-divider" />

                  {/* New Password */}
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    <label className="dd-section-label" style={{ margin: 0 }}>
                      New Password
                    </label>
                    <input
                      type="password"
                      className="dd-input"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </div>

                  <hr className="dd-section-divider" />

                  {/* Confirm New Password */}
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    <label className="dd-section-label" style={{ margin: 0 }}>
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      className="dd-input"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>

                  <hr className="dd-section-divider" />

                  {/* Two-Factor Authentication */}
                   {/*
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <p className="dd-section-label" style={{ margin: 0 }}>
                        Two Factor Authentication (2FA)
                      </p>
                      <p className="dd-section-sublabel">
                        Include more security.
                      </p>
                    </div>
                    <Toggle
                      checked={twoFactorEnabled}
                      onChange={handleToggle2FA}
                    />
                  </div>

                  <hr className="dd-section-divider" />
                    */}
                  {/* Login History */}
                  {/*
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    <div>
                      <p className="dd-section-label" style={{ margin: 0 }}>
                        Where you're logged in
                      </p>
                      <p className="dd-section-sublabel">
                        We'll alert you via{" "}
                        <strong>{userEmail || "pm@think-ai.io"}</strong>
                      </p>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <div className="dd-login-item">
                        <Monitor size={20} color="#64748B" />
                        <div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 600,
                                fontSize: 14,
                                color: "#0F172A",
                              }}
                            >
                              2025 MacBook Pro 14-inch
                            </span>
                            <span
                              style={{
                                background: "#F0FDF4",
                                color: "#166534",
                                padding: "2px 8px",
                                borderRadius: 12,
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              Active now
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              color: "#64748B",
                              marginTop: 2,
                            }}
                          >
                            Southampton, United Kingdom • 16 Oct at 12:00 pm
                          </div>
                        </div>
                      </div>
                      <div className="dd-login-item">
                        <Monitor size={20} color="#64748B" />
                        <div>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 14,
                              color: "#0F172A",
                            }}
                          >
                            HP Spectre x360
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              color: "#64748B",
                              marginTop: 2,
                            }}
                          >
                            Berlin, Germany • 19 Oct at 09:15 pm
                          </div>
                        </div>
                      </div>
                      <div className="dd-login-item">
                        <Monitor size={20} color="#64748B" />
                        <div>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 14,
                              color: "#0F172A",
                            }}
                          >
                            MacBook Air M2
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              color: "#64748B",
                              marginTop: 2,
                            }}
                          >
                            Tokyo, Japan • 18 Oct at 02:50 am
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  */}
                </>
              )}
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
      {isToastMsgOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeToastMsg}
        >
          <ToastMsg onClose={closeToastMsg} message={toastMessage} />
        </PortalPopup>
      )}
    </>
  );
};

export default SettingsAccount3Page;
